import { useMutation, useQuery } from '@apollo/client';

import { camelCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import ImportExportConfig from '../../../document/components/ImportExportConfig.tsx';
import { customKebabCase } from '../../../document/pages/Configuration';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SecondaryNavigation from '../../../core/components/SecondaryNavigation.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import { canSeeSupportUserConfigs } from '../../../users/utils.ts';
import { VOICE_AGENT_CONFIG_STEPS, VOICE_AGENT_FORM_DEFAULT_VALUES } from '../components/ConfigSteps.tsx';
import {
    CALLER_VERIFICATION_ATTEMPTS_DEFAULT,
    CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER,
    MAX_CALL_DURATION_DEFAULT,
    VOICE_AGENT_CONFIG_PATH,
    VOICE_AGENT_PATH,
    VOICE_MODEL_CONVERSATIONAL,
    VOICE_SPEED_DEFAULT,
} from '../constants.ts';
import useTeamOptions from '../hooks/useTeamOptions.ts';
import {
    DELETE_VOICE_AGENT_KNOWLEDGE_FILE,
    EXPORT_VOICE_AGENT_CONFIG,
    GET_VOICE_AGENT,
    IMPORT_VOICE_AGENT_CONFIG,
    UPDATE_VOICE_AGENT,
    UPLOAD_VOICE_AGENT_KNOWLEDGE_FILE,
} from '../queries.ts';
import { agentEditablePromptToFormValues } from '../utils/editablePrompt.ts';
import { transformVoiceAgentFormData } from '../utils/transformVoiceAgentFormData.ts';

const CONFIG_TABS = ['details', 'voice', 'tools', 'notifications', 'prompt', 'knowledge'];
// Workist only, like the channel config transfer it reuses
const SUPPORT_CONFIG_TABS = ['importExport'];

const ConfigPage = (props) => {
    const { channelId, tab = 'details' } = useParams();
    const { t } = useTranslation('voice_agents');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();

    const teamOptions = useTeamOptions();
    const form = useForm({ defaultValues: VOICE_AGENT_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const [originalFiles, setOriginalFiles] = useState<any[]>([]);

    const { data, loading, previousData, refetch } = useQuery(GET_VOICE_AGENT, {
        fetchPolicy: 'no-cache',
        variables: { id: channelId },
    });
    const agent = (loading ? previousData : data)?.voiceAgent;

    useEffect(() => {
        const knowledgeFiles = agent?.knowledgeFiles || [];
        setOriginalFiles(knowledgeFiles);
        form.reset({
            name: agent?.name || '',
            teamId: agent?.team?.id || undefined,
            language: agent?.language || 'de',
            introSpeech: agent?.introSpeech || '',
            introSpeechPersonalized: agent?.introSpeechPersonalized || '',
            consentQuestion: agent?.consentQuestion || '',
            phoneNumber: agent?.phoneNumber || '',
            transferPhoneNumber: agent?.transferPhoneNumber || '',
            fallbackEmail: agent?.fallbackEmail || '',
            sendEmailAllowedDomains: agent?.sendEmailAllowedDomains || [],
            callerIdentificationLookupDefinitionId: agent?.callerIdentificationLookupDefinitionId || '',
            orderHistoryLookupDefinitionId: agent?.orderHistoryLookupDefinitionId || '',
            orderLookupDefinitionId: agent?.orderLookupDefinitionId || '',
            articleLookupDefinitionId: agent?.articleLookupDefinitionId || '',
            erpToolProviderKey: agent?.erpToolProviderKey || '',
            erpToolProviderIntegrationId: agent?.erpToolProviderIntegrationId || '',
            requireIdentifiedCaller: agent?.requireIdentifiedCaller ?? false,
            callerRejectionMessage: agent?.callerRejectionMessage || '',
            requireCallerVerification: agent?.requireCallerVerification ?? false,
            callerVerificationField: agent?.callerVerificationField || CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER,
            callerVerificationExtraDataKey: agent?.callerVerificationExtraDataKey || '',
            callerVerificationQuestion: agent?.callerVerificationQuestion || '',
            callerVerificationMaxAttempts: String(
                agent?.callerVerificationMaxAttempts ?? CALLER_VERIFICATION_ATTEMPTS_DEFAULT
            ),
            callerVerificationFailedMessage: agent?.callerVerificationFailedMessage || '',
            backgroundVoiceDetection: agent?.backgroundVoiceDetection ?? true,
            voiceModel: agent?.voiceModel || VOICE_MODEL_CONVERSATIONAL,
            voiceSpeed: agent?.voiceSpeed ?? VOICE_SPEED_DEFAULT,
            maxCallDurationSeconds: agent?.maxCallDurationSeconds ?? MAX_CALL_DURATION_DEFAULT,
            forwardOnConsentDenial: agent?.forwardOnConsentDenial ?? false,
            webCallRequestsEnabled: agent?.webCallRequestsEnabled ?? false,
            webCallAllowedPrefixes: (agent?.webCallAllowedPrefixes || []).join(', '),
            webCallDailyCap: String(agent?.webCallDailyCap ?? 50),
            webCallCooldownSeconds: String(agent?.webCallCooldownSeconds ?? 3600),
            webCallAskConsentQuestion: agent?.webCallAskConsentQuestion ?? false,
            calendlyMeetingLink: agent?.calendlyMeetingLink || '',
            calendlySummaryQuestion: agent?.calendlySummaryQuestion || '',
            // the token is write-only: the form field always starts empty; a non-empty value replaces it
            calendlyApiToken: '',
            calendlyApiTokenSet: agent?.calendlyApiTokenSet ?? false,
            summaryEmailEnabled: agent?.summaryEmailEnabled ?? false,
            summaryEmailRecipients: agent?.summaryEmailRecipients || [],
            summaryTeamsEnabled: agent?.summaryTeamsEnabled ?? false,
            summaryTeamsTeamId: agent?.summaryTeamsTeamId || '',
            summaryTeamsTeamName: agent?.summaryTeamsTeamName || '',
            summaryTeamsChannelId: agent?.summaryTeamsChannelId || '',
            summaryTeamsChannelName: agent?.summaryTeamsChannelName || '',
            enabledTools: agent?.enabledTools || [],
            defaultChannels: agent?.defaultChannels
                ? {
                      orderChannelId: agent.defaultChannels.orderChannelId || '',
                      rfqChannelId: agent.defaultChannels.rfqChannelId || '',
                  }
                : null,
            documentFields: {
                order: agent?.documentFields?.order
                    ? {
                          headerFields: agent.documentFields.order.headerFields || [],
                          lineItemFields: agent.documentFields.order.lineItemFields || [],
                      }
                    : null,
                rfq: agent?.documentFields?.rfq
                    ? {
                          headerFields: agent.documentFields.rfq.headerFields || [],
                          lineItemFields: agent.documentFields.rfq.lineItemFields || [],
                      }
                    : null,
            },
            editablePrompt: agentEditablePromptToFormValues(agent?.editablePrompt),
            // while the agent is still loading nothing is "legacy" yet — the banner only shows for a loaded agent
            editablePromptEnabled: agent ? Boolean(agent.editablePromptEnabled) : true,
            files: knowledgeFiles,
        });
    }, [agent]);

    const [updateVoiceAgent] = useMutation(UPDATE_VOICE_AGENT);
    const [uploadKnowledgeFile] = useMutation(UPLOAD_VOICE_AGENT_KNOWLEDGE_FILE);
    const [deleteKnowledgeFile] = useMutation(DELETE_VOICE_AGENT_KNOWLEDGE_FILE);
    const [exportConfig] = useMutation(EXPORT_VOICE_AGENT_CONFIG);
    const [importConfig] = useMutation(IMPORT_VOICE_AGENT_CONFIG);

    const handleExport = () => exportConfig({ variables: { id: channelId } });
    const handleImport = (file: File) => importConfig({ variables: { id: channelId, file } }).then(() => refetch());

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (formData) => {
        setIsSubmitting(true);
        try {
            const currentFiles = formData.files || [];
            const isNewFile = (file: File | any): file is File => file instanceof File;
            const filesToDelete = originalFiles.filter(
                (orig) => !currentFiles.some((f: any) => !isNewFile(f) && f.id === orig.id)
            );
            const filesToUpload = currentFiles.filter(isNewFile);

            for (const file of filesToDelete) {
                await deleteKnowledgeFile({ variables: { configId: channelId, documentationId: file.id } });
            }
            for (const file of filesToUpload) {
                await uploadKnowledgeFile({ variables: { configId: channelId, file } });
            }

            await updateVoiceAgent({ variables: { id: channelId, input: transformVoiceAgentFormData(formData) } });
            await refetch();
            publishToast({ description: t('configPage.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('configPage.updateError'), status: 'error' });
            await refetch();
        } finally {
            setIsSubmitting(false);
        }
    };

    const config = VOICE_AGENT_CONFIG_STEPS[camelCase(tab)];
    const ConfigComponent = config?.component;
    const showSupportTabs = canSeeSupportUserConfigs(user);
    const isImportExportTab = showSupportTabs && camelCase(tab) === 'importExport';

    return (
        <>
            <Layout>
                <Page>
                    <Page.Header className="flex items-center">
                        <Link
                            className="flex flex-shrink min-w-0 gap-3 rounded transition-colors hover:bg-surface-strong py-1 px-2 -mx-2 items-center cursor-pointer select-text font-semibold text-base text-content-medium hover:text-content-strong"
                            to={url(VOICE_AGENT_PATH, { channelId: agent?.id || '' })}
                        >
                            {agent?.name}
                        </Link>
                        <Page.HeaderSeparator />
                        <Page.HeaderTitle>{t('configPage.title')}</Page.HeaderTitle>
                    </Page.Header>

                    <div className="flex flex-col md:flex-row flex-1 min-h-0">
                        <SecondaryNavigation {...props}>
                            <SecondaryNavigation.List>
                                <SecondaryNavigation.SectionLabel>
                                    {t('configPage.navigation.generalSection')}
                                </SecondaryNavigation.SectionLabel>
                                {CONFIG_TABS.map((configOption) => (
                                    <ConfigNavigationButton key={configOption} configOption={configOption}>
                                        {t(`configPage.navigation.${configOption}SectionTitle`)}
                                    </ConfigNavigationButton>
                                ))}
                                {showSupportTabs && (
                                    <>
                                        <SecondaryNavigation.SectionLabel>
                                            {t('configPage.navigation.otherSection')}
                                        </SecondaryNavigation.SectionLabel>
                                        {SUPPORT_CONFIG_TABS.map((configOption) => (
                                            <ConfigNavigationButton key={configOption} configOption={configOption}>
                                                {t(`configPage.navigation.${configOption}SectionTitle`)}
                                            </ConfigNavigationButton>
                                        ))}
                                    </>
                                )}
                            </SecondaryNavigation.List>
                        </SecondaryNavigation>

                        <Page.Content lowered>
                            {isImportExportTab && agent && (
                                <ImportExportConfig
                                    channel={agent}
                                    documentConfiguration={{ documentTypeName: 'voiceAgent' }}
                                    onImport={handleImport}
                                    onExport={handleExport}
                                    exportableConfigs={['voiceAgentConfig']}
                                    exportResponseKey="exportVoiceAgentConfig"
                                    exportInfoAlert={t('configPage.importExport.exportInfoAlert')}
                                />
                            )}
                            {ConfigComponent && (
                                <SettingsFormCard
                                    onSubmit={form.handleSubmit(handleSubmit)}
                                    title={t(config.title)}
                                    description={t(config.description)}
                                    // the Tools tab needs more room for its category rail + detail panel, the Prompt
                                    // tab for the phase cards and the floating palette next to them
                                    className={
                                        { tools: 'max-w-[64rem]', prompt: 'max-w-320' }[camelCase(tab)] ?? undefined
                                    }
                                >
                                    <ConfigComponent form={form} teamOptions={teamOptions} />
                                    <div className="flex justify-end">
                                        <SaveStatus
                                            isDirty={form.formState.isDirty}
                                            isSubmitting={isSubmitting}
                                            errorCount={countFieldErrors(form.formState.errors)}
                                            isDisabled={config.isValid ? !config.isValid(form) : false}
                                        />
                                    </div>
                                </SettingsFormCard>
                            )}
                        </Page.Content>
                    </div>
                </Page>
            </Layout>
        </>
    );
};

const ConfigNavigationButton = ({ configOption, children }) => {
    const { channelId, tab = 'details' } = useParams();
    return (
        <SecondaryNavigation.Button asChild active={tab === customKebabCase(configOption)}>
            <Link to={url(VOICE_AGENT_CONFIG_PATH, { channelId, tab: customKebabCase(configOption) })}>{children}</Link>
        </SecondaryNavigation.Button>
    );
};

export default ConfigPage;
